import { useMemo, forwardRef, MutableRefObject } from 'react'
import soft from 'timezone-soft'
import spacetime from 'spacetime'
import allTimezones from './timezoneList'
import cx from 'classnames'
import Styles from './TextField.module.scss'
import { useField } from 'formik'
import { useScrollToError } from './ScrollToError'

export interface TimezoneOption {
  value: string
  label: string
  abbrev?: string
  altName?: string
  offset?: number
}

export type Timezone = TimezoneOption | string

export interface Props {
  value: string
  onChange: (timezone: string) => void
  error?: boolean
  placeholder?: string
}

export const TimezoneSelect = forwardRef<HTMLSelectElement, Props>(
  ({ value, onChange, error = false, placeholder = 'Select a timezone' }, ref) => {
    const getOptions = useMemo(() => {
      return Object.entries(allTimezones)
        .reduce<TimezoneOption[]>((selectOptions, zone) => {
          const now = spacetime.now(zone[0])
          const tz = now.timezone()
          const tzStrings = soft(zone[0])

          let abbr = now.isDST() ? tzStrings[0].daylight?.abbr : tzStrings[0].standard?.abbr
          let altName = now.isDST() ? tzStrings[0].daylight?.name : tzStrings[0].standard?.name

          const min = tz.current.offset * 60
          const hr = `${(min / 60) ^ 0}:` + (min % 60 === 0 ? '00' : Math.abs(min % 60))
          const prefix = min ? `(UTC${hr.includes('-') ? hr : `+${hr}`})` : '(UTC)'
          const label = `${prefix} ${zone[1]}`

          selectOptions.push({
            value: tz.name,
            label: label,
            offset: tz.current.offset,
            abbrev: abbr,
            altName: altName,
          })

          return selectOptions
        }, [])
        .sort((a, b) => {
          // @ts-ignore
          return a.offset - b.offset
        })
    }, [])

    const findFuzzyTz = (zone: string): TimezoneOption | undefined => {
      let currentTime = spacetime.now('GMT')
      try {
        currentTime = spacetime.now(zone)
      } catch (err) {
        return
      }
      return getOptions
        .filter((tz) => tz.offset === currentTime.timezone().current.offset)
        .map((tz) => {
          let score = 0
          if (
            currentTime.timezones[tz.value.toLowerCase()] &&
            !!currentTime.timezones[tz.value.toLowerCase()].dst === currentTime.timezone().hasDst
          ) {
            if (tz.value.toLowerCase().indexOf(currentTime.tz.substr(currentTime.tz.indexOf('/') + 1)) !== -1) {
              score += 8
            }
            if (tz.label.toLowerCase().indexOf(currentTime.tz.substr(currentTime.tz.indexOf('/') + 1)) !== -1) {
              score += 4
            }
            if (tz.value.toLowerCase().indexOf(currentTime.tz.substr(0, currentTime.tz.indexOf('/')))) {
              score += 2
            }
            score += 1
          } else if (tz.value === 'GMT') {
            score += 1
          }
          return { tz, score }
        })
        .sort((a, b) => b.score - a.score)
        .map(({ tz }) => tz)[0]
    }

    const parseTimezone = (zone: string): string | undefined | false =>
      getOptions.find((tz) => tz.value === zone)?.value || (zone.indexOf('/') !== -1 && findFuzzyTz(zone)?.value)

    return (
      <div className={Styles.select}>
        <select
          ref={ref}
          className={cx(Styles.input, { [Styles.error]: error }, { [Styles.emptySelectField]: !value })}
          value={parseTimezone(value) || ''}
          onChange={({ target: { value } }) => onChange(value)}
        >
          <option value="">{placeholder}</option>
          {getOptions.map((timezoneOption) => (
            <option key={timezoneOption.value} value={timezoneOption.value}>
              {timezoneOption.label}
            </option>
          ))}
        </select>
      </div>
    )
  },
)

export const TimezoneSelectField = forwardRef<HTMLSelectElement, any>(
  ({ name, value, label, onChange, errorMessage, className, placeholder }, ref) => (
    <div className={cx(Styles.textField, className)}>
      <label className={Styles.label}>{label}</label>
      <TimezoneSelect ref={ref} value={value} onChange={onChange} placeholder={placeholder} />
      {errorMessage && <div className={Styles.errorMessage}>{errorMessage}</div>}
    </div>
  ),
)

export const FormikTimezoneSelectField = ({
  name,
  label,
  className,
  placeholder,
}: {
  name: string
  label: string
  className: string
  placeholder: string
}) => {
  const [field, meta, { setValue }] = useField(name)
  const displayError = meta.touched && meta.error
  const { inputRef } = useScrollToError(name)

  return (
    <TimezoneSelectField
      ref={inputRef as MutableRefObject<HTMLSelectElement>}
      label={label}
      name={name}
      value={field.value}
      onChange={setValue}
      errorMessage={displayError}
      className={className}
      placeholder={placeholder}
    />
  )
}
